import { KeyboardEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Column, useTable } from 'react-table';
import { Dispatch } from 'redux';
import { getKontrakte, KontraktuebersichtSelectors } from '../../store/Kontraktuebersicht.store';
import { mobileQuery } from '../BestelluebersichtUndAuftragskorb/BestelluebersichtUndAuftragskorb';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../../shared/ui-components/LoadingSpinner/LoadingSpinner';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import { usePersistedState } from '../../shared/hooks/usePersistedState';
import { useScrollRestoration } from '../../shared/hooks/useScrollRestoration';
import { PAGE_TITLE_KONTRAKTE, routes } from '../../shared/constants';
import { Filter, UebersichtKontrakt } from '../../shared/types';
import {
    kontraktAbschlussmengeColumn,
    kontraktansichtLinkColumn,
    kontraktColumn,
    kontraktLieferDatumAbColumn,
    kontraktLieferDatumBisColumn,
    kontraktpartnerColumn,
    kontraktPreislistenArt,
    kontraktRestmengeColumn,
    kontraktWeNameColumn,
} from './Columns';
import { useMediaQuery } from 'react-responsive';
import kontraktStyles from './Kontraktuebersicht.module.scss';
import styles from '../BestelluebersichtUndAuftragskorb/BestelluebersichtUndAuftragskorb.module.scss';
import TextInputWithoutFormik from '../../shared/ui-components/TextInput/TextInputWithoutFormik';
import { KontraktuebersichtHeader } from './KontraktuebersichtHeader';
import { FilterWithOptions, FormattedValueAfterSelectionWE, kontraktpartnerOptionen, warenempfaengerOptionen } from './FilterWithOptions';
import { OverviewListBody } from '../../shared/ui-components/OverviewList/OverviewListBody';
import KontraktUebersichtTable from './KontraktUebersichtTable/KontraktUebersichtTable';
import { useHistory } from 'react-router-dom';
import { Option } from '../../shared/helper/select-options';
import { fromKilogramm, isBekannteMengenEinheit, NORMALIZED_UNIT_TONNE, summeNachEinheit } from './KontraktuebersichtMengenHelper';

const loadKontrakte = (
    dispatch: Dispatch,
    kontraktNummer?: string,
    preislistenArt?: string,
    warenempfaengerNummer?: string,
    kontraktPartnerNummer?: string
) => {
    const filter: Filter = {};
    if (kontraktNummer) {
        filter.kontraktNummer = kontraktNummer;
    }

    if (preislistenArt) {
        filter.preislistenArt = preislistenArt;
    }

    if (warenempfaengerNummer) {
        filter.warenempfaengerNummer = warenempfaengerNummer;
    }

    if (kontraktPartnerNummer) {
        filter.kontraktPartnerNummer = kontraktPartnerNummer;
    }

    dispatch(
        getKontrakte({
            filter,
        })
    );
};

const MOBILE_COLUMNS = [
    'kontraktNummer',
    'kontraktpartnerName',
    'rechnungsempfaengerName',
    'preislistenArt',
    'lieferDatumAb',
    'lieferDatumBis',
    'abschlussmenge',
    'restmenge',
];

const createGesamtRow = (kontrakte: UebersichtKontrakt[]): (UebersichtKontrakt & { isTotalRow: boolean }) | null => {
    const kontrakteMitNummer = kontrakte.filter((kontrakt) => !!kontrakt.kontraktNummer);

    if (!kontrakteMitNummer.length) {
        return null;
    }

    // Nur Kontrakte mit bekannter Einheit (Tonnen, KGM) in die Summen einbeziehen
    const abschlussItems = kontrakteMitNummer
        .filter((k) => isBekannteMengenEinheit(k.abschlussmengeEinheit) && k.abschlussmenge != null)
        .map((k) => ({ menge: k.abschlussmenge ?? 0, einheit: k.abschlussmengeEinheit }));
    const restItems = kontrakteMitNummer
        .filter((k) => isBekannteMengenEinheit(k.restmengeEinheit) && k.restmenge != null)
        .map((k) => ({ menge: k.restmenge ?? 0, einheit: k.restmengeEinheit }));

    const { summeKg: abschlussSummeKg } = summeNachEinheit(abschlussItems);
    const { summeKg: restSummeKg } = summeNachEinheit(restItems);

    // Keine Gesamtzeile, wenn weder Abschluss- noch Restmenge (mit bekannter Einheit) vorkommt
    if (abschlussSummeKg === 0 && restSummeKg === 0) {
        return null;
    }

    // Anzeige einheitlich in Tonnen (de-DE, 2 Dezimalstellen wie Gruppensummen)
    const abschlussDisplay = fromKilogramm(abschlussSummeKg, NORMALIZED_UNIT_TONNE);
    const restDisplay = fromKilogramm(restSummeKg, NORMALIZED_UNIT_TONNE);
    const abschlussmenge = Math.round(abschlussDisplay.wert * 100) / 100;
    const restmenge = Math.round(restDisplay.wert * 100) / 100;

    return {
        kontraktNummer: '',
        kontraktpartnerName: '',
        kontraktpartnerNummer: '',
        abschlussmenge,
        abschlussmengeEinheit: abschlussSummeKg > 0 ? 't' : '',
        restmenge,
        restmengeEinheit: restItems.length > 0 ? 't' : '',
        lieferDatumAb: '',
        lieferDatumBis: '',
        preislistenArt: '',
        warenempfaengerName: '',
        warenempfaengerNummer: '',
        allWarenempfaenger: [],
        anzahlWarenempfaenger: 0,
        lastModifiedAt: '',
        isTotalRow: true,
    };
};

const getKontrakteColumns = (isMobile: boolean): Column<UebersichtKontrakt>[] => {
    return [
        kontraktColumn(isMobile),
        kontraktpartnerColumn(isMobile),
        kontraktPreislistenArt(isMobile),
        kontraktWeNameColumn(isMobile),
        kontraktLieferDatumAbColumn(isMobile),
        kontraktLieferDatumBisColumn(isMobile),
        kontraktAbschlussmengeColumn(isMobile),
        kontraktRestmengeColumn(isMobile),
        kontraktansichtLinkColumn(),
    ];
};

export const Kontraktuebersicht = (): ReactElement => {
    const kontrakte = useSelector(KontraktuebersichtSelectors.kontrakte);
    const isLoading = useSelector(KontraktuebersichtSelectors.kontrakteLoading);
    const loadedFailed = useSelector(KontraktuebersichtSelectors.kontrakteLoadFailed);
    const history = useHistory();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery({ query: mobileQuery });

    useDocumentTitle(PAGE_TITLE_KONTRAKTE);

    const [kontraktNummerFilter, setKontraktNummerFilter] = usePersistedState<string>('Kontraktuebersicht.kontraktNummer', '');
    const [kontraktNummerFilterInternalValue, setKontraktNummerFilterInternalValue] = useState(kontraktNummerFilter);
    const [preislistenArtFilter, setPreislistenArtFilter] = usePersistedState<string>('Kontraktuebersicht.preislistenArt', '');
    const [preislistenArtFilterInternalValue, setPreislistenArtFilterInternalValue] = useState(preislistenArtFilter);
    // Optionen mit JSX-Label sind nicht JSON-serialisierbar -> wir merken nur die Nummer und
    // rekonstruieren die volle Option zur Render-Zeit aus den geladenen Daten.
    const [warenempfaengerFilterNummer, setWarenempfaengerFilterNummer] = usePersistedState<string | undefined>(
        'Kontraktuebersicht.warenempfaengerNummer',
        undefined
    );
    const [kontraktpartnerFilterNummer, setKontraktpartnerFilterNummer] = usePersistedState<string | undefined>(
        'Kontraktuebersicht.kontraktpartnerNummer',
        undefined
    );

    const warenempfaengerOptions = useMemo(() => warenempfaengerOptionen(kontrakte), [kontrakte]);
    const kontraktpartnerOptions = useMemo(() => kontraktpartnerOptionen(kontrakte), [kontrakte]);

    const warenempfaengerNummerFilter = useMemo<Option | undefined>(
        () => warenempfaengerOptions.find((option) => option.nummer === warenempfaengerFilterNummer),
        [warenempfaengerFilterNummer, warenempfaengerOptions]
    );
    const kontraktpartnerNummerFilter = useMemo<Option | undefined>(
        () => kontraktpartnerOptions.find((option) => option.nummer === kontraktpartnerFilterNummer),
        [kontraktpartnerFilterNummer, kontraktpartnerOptions]
    );

    useScrollRestoration('Kontraktuebersicht', !isLoading && kontrakte.length > 0);

    const columns: Column<UebersichtKontrakt>[] = useMemo(() => getKontrakteColumns(isMobile), [isMobile]);

    const kontrakteMitGesamtzeile = useMemo(() => {
        const gesamtRow = createGesamtRow(kontrakte);

        if (!gesamtRow) {
            return kontrakte;
        }

        return [...kontrakte, gesamtRow];
    }, [kontrakte]);

    const tableInstance = useTable<UebersichtKontrakt>({
        columns,
        data: kontrakteMitGesamtzeile,
    });

    const { state } = tableInstance;

    const onFilterKeyDown = (event: KeyboardEvent, setFunction: VoidFunction): void => {
        if (event.key === 'Enter' || event.code === 'Enter') {
            setFunction();
        }
    };

    useEffect(() => {
        loadKontrakte(dispatch, kontraktNummerFilter, preislistenArtFilter, warenempfaengerFilterNummer, kontraktpartnerFilterNummer);
    }, [
        dispatch,
        kontraktNummerFilter,
        preislistenArtFilter,
        state.pageIndex,
        state.pageSize,
        kontraktpartnerFilterNummer,
        warenempfaengerFilterNummer,
    ]);

    return (
        <>
            <KontraktuebersichtHeader
                visible={loadedFailed}
                actionCallback={() =>
                    loadKontrakte(
                        dispatch,
                        kontraktNummerFilter,
                        preislistenArtFilter,
                        warenempfaengerFilterNummer,
                        kontraktpartnerFilterNummer
                    )
                }
            />

            <LoadingSpinner isLoading={isLoading && kontrakte.length === 0}>
                <>
                    <div className={styles._panelAboveTable}>
                        <div className={styles._filterPanel}>
                            <div className={kontraktStyles._kontraktFilter}>
                                <TextInputWithoutFormik
                                    name="kontraktNummerFilter"
                                    id="kontraktNummerFilter"
                                    placeholder="Kontraktnummer Filter"
                                    value={kontraktNummerFilterInternalValue}
                                    onKeyDown={(event) =>
                                        onFilterKeyDown(event, () => setKontraktNummerFilter(kontraktNummerFilterInternalValue))
                                    }
                                    onFieldChange={setKontraktNummerFilterInternalValue}
                                    onFieldBlur={setKontraktNummerFilter}
                                />
                            </div>
                            <FilterWithOptions
                                className={kontraktStyles._kontraktFilter}
                                options={kontraktpartnerOptions}
                                value={kontraktpartnerNummerFilter ?? null}
                                onChange={(option: Option | null): void => setKontraktpartnerFilterNummer(option?.nummer)}
                                loading={isLoading}
                                name={'kontraktpartnerNummerFilter'}
                                placeholder={'Kontraktpartner Filter'}
                            />
                            <div className={kontraktStyles._kontraktFilter}>
                                <TextInputWithoutFormik
                                    name="preislistenArtFilter"
                                    id="preislistenArtFilter"
                                    placeholder="Preislistenart Filter"
                                    value={preislistenArtFilterInternalValue}
                                    onKeyDown={(event) =>
                                        onFilterKeyDown(event, () => setPreislistenArtFilter(preislistenArtFilterInternalValue))
                                    }
                                    onFieldChange={setPreislistenArtFilterInternalValue}
                                    onFieldBlur={setPreislistenArtFilter}
                                />
                            </div>
                            <FilterWithOptions
                                className={kontraktStyles._kontraktFilter}
                                options={warenempfaengerOptions}
                                value={warenempfaengerNummerFilter ?? null}
                                onChange={(option: Option | null): void => setWarenempfaengerFilterNummer(option?.nummer)}
                                loading={isLoading}
                                name={'warenempfaengerNummerFilter'}
                                placeholder={'Warenempfänger Filter'}
                                formattedValue={FormattedValueAfterSelectionWE}
                            />
                        </div>
                    </div>
                    {isMobile ? (
                        <OverviewListBody
                            tableInstance={tableInstance}
                            mobileColumns={MOBILE_COLUMNS}
                            isPaginationOn={false}
                            onRowClick={(row): void => {
                                history.push(`${routes.kontraktansicht}/${row.original['kontraktNummer']}`);
                            }}
                        />
                    ) : (
                        <KontraktUebersichtTable {...tableInstance} />
                    )}
                </>
            </LoadingSpinner>
        </>
    );
};
